import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Clock, Lock, ListChecks, ArrowRight, Check } from "lucide-react";

const BG_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/gwkjr2km_home2.jpg";
const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_mls-deadline-master/artifacts/jbrp9xqq_equitasfit%20%28Logo%29%20%284%29.png";

const TIMELINES = [
  "Just exploring",
  "Within 3 months",
  "3–6 months",
  "6–12 months",
  "Right away",
];

export default function Landing() {
  useDocumentTitle();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "", timeline: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("We'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      {/* Nav */}
      <header className="sticky top-0 z-40 w-full border-b border-stone-200 bg-[#faf7f2]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Equitas 1%" className="h-11 w-11 rounded-full object-cover shadow-sm ring-1 ring-stone-200" />
            <div className="flex flex-col leading-none">
              <span className="overline text-stone-500">Equitas Homes</span>
              <span className="text-lg font-heading font-semibold tracking-tight text-stone-900 mt-1">
                Listing Workflow
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-login"
              className="text-sm font-medium text-stone-700 hover:text-stone-900 px-3"
            >
              Log in
            </Link>
            <Button
              data-testid="nav-signup"
              onClick={() => nav("/signup")}
              className="h-10 rounded-full px-5 text-white"
              style={{ backgroundColor: "#010082" }}
            >
              Agent sign up
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <p className="overline text-stone-500 mb-6">1% List Fee · Full Service</p>
          <h1 className="font-heading font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-stone-900">
            Every Home We're Guiding To <span className="font-serif-italic text-brand">The Closing Table</span>
          </h1>
          <p className="mt-6 text-stone-600 text-base leading-relaxed max-w-xl">
            A guided seven-phase workflow so nothing slips — from listing appointment to closing.
            Priced at 1%. Delivered with the detail of a boutique brokerage.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <span className="chip"><Clock className="h-3.5 w-3.5" /> Full-service · 1% List Fee</span>
            <span className="chip"><Lock className="h-3.5 w-3.5" /> Private &amp; confidential</span>
            <span className="chip"><ListChecks className="h-3.5 w-3.5" /> 7 short phases</span>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href="#lead-form"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-1.5 h-11 rounded-full px-5 text-white font-medium text-sm"
              style={{ backgroundColor: "#010082" }}
            >
              Thinking of selling? Let's talk
              <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/login"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center h-11 rounded-full border border-stone-300 px-5 font-medium text-sm text-stone-800 hover:bg-stone-100"
            >
              Agent log in
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative rounded-md overflow-hidden aspect-[4/5]">
            <img src={BG_URL} alt="Elegant living room" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-4 left-4">
              <span className="chip chip-dark">Curb appeal · Cottage</span>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6"><div className="hr-serif" /></div>

      {/* What's inside — 3-column value props */}
      <section className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20">
        <p className="overline text-stone-500 mb-4">What's inside</p>
        <h2 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight text-stone-900 max-w-2xl">
          Every detail we need to <span className="font-serif-italic text-brand">sell your home</span> with confidence.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              n: "01",
              title: "Guided intake",
              body: "We capture everything up front — timeline, goals, features, disclosures — so pricing and marketing land right.",
            },
            {
              n: "02",
              title: "Deadline-aware workflow",
              body: "Seven phases with per-task deadlines. Overdue tasks surface immediately, upcoming ones stay one click away.",
            },
            {
              n: "03",
              title: "Transparent to you",
              body: "Watch your listing progress on your own Equitas share link — the same source of truth we use behind the scenes.",
            },
          ].map((c) => (
            <div key={c.n} className="rounded-md border border-stone-200 bg-white p-7">
              <span className="section-index text-stone-500 text-sm">Section {c.n}</span>
              <h3 className="mt-3 text-xl font-heading font-semibold tracking-tight text-stone-900">{c.title}</h3>
              <p className="mt-2 text-sm text-stone-600 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-6"><div className="hr-serif" /></div>

      {/* Lead form */}
      <section id="lead-form" className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="overline text-stone-500 mb-4">Get in touch</p>
          <h2 className="text-3xl sm:text-4xl font-heading font-medium tracking-tight text-stone-900 leading-[1.1]">
            Ready to talk about <span className="font-serif-italic text-brand">selling your home?</span>
          </h2>
          <p className="mt-5 text-stone-600 text-base leading-relaxed">
            Tell us a bit about the property and your timeline. Louis will reach out personally within one business day.
          </p>

          <div className="mt-10 rounded-md border border-stone-200 bg-white p-5 flex items-start gap-4">
            <img src={LOGO_URL} alt="Louis Fong" className="h-14 w-14 rounded-full object-cover shrink-0 shadow-sm" />
            <div>
              <p className="text-sm font-medium text-stone-900">Louis Fong</p>
              <p className="text-xs text-stone-500 mt-0.5">Realtor · Equitas Homes</p>
              <p className="font-serif-italic text-stone-700 text-sm leading-relaxed mt-2.5">
                "Sharing the full picture up front is the biggest lift for your sale."
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-md border border-stone-200 bg-white p-7 lg:p-10">
            {sent ? (
              <div data-testid="lead-form-success" className="py-8 text-center">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full mb-4" style={{ backgroundColor: "#010082" }}>
                  <Check className="h-6 w-6 text-white" strokeWidth={2.5} />
                </div>
                <h3 className="text-2xl font-heading font-semibold tracking-tight text-stone-900">
                  Thank you — we'll be in touch.
                </h3>
                <p className="mt-2 text-sm text-stone-500 max-w-sm mx-auto">
                  Louis will reach out personally within one business day.
                </p>
                <Button
                  onClick={() => setSent(false)}
                  variant="outline"
                  className="mt-6 rounded-full border-stone-300"
                  data-testid="lead-form-send-another"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="lname" className="text-stone-700">Full name</Label>
                  <Input id="lname" data-testid="lead-name" value={form.name} onChange={set("name")} required placeholder="Jane Doe" className="h-11 bg-[#faf7f2]/40 border-stone-300" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lemail" className="text-stone-700">Email</Label>
                  <Input id="lemail" data-testid="lead-email" type="email" value={form.email} onChange={set("email")} required placeholder="you@email.com" className="h-11 bg-[#faf7f2]/40 border-stone-300" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lphone" className="text-stone-700">Phone</Label>
                  <Input id="lphone" data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" className="h-11 bg-[#faf7f2]/40 border-stone-300" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="laddress" className="text-stone-700">Property address</Label>
                  <Input id="laddress" data-testid="lead-address" value={form.address} onChange={set("address")} placeholder="123 Elm St, Austin, TX" className="h-11 bg-[#faf7f2]/40 border-stone-300" />
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="ltimeline" className="text-stone-700">Timeline</Label>
                  <Select value={form.timeline} onValueChange={(v) => setForm((f) => ({ ...f, timeline: v }))}>
                    <SelectTrigger id="ltimeline" data-testid="lead-timeline" className="h-11 bg-[#faf7f2]/40 border-stone-300">
                      <SelectValue placeholder="When are you hoping to sell?" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="lmessage" className="text-stone-700">What should we know?</Label>
                  <Textarea id="lmessage" data-testid="lead-message" rows={4} value={form.message} onChange={set("message")} placeholder="A few sentences about your home and what a successful sale looks like." className="bg-[#faf7f2]/40 border-stone-300" />
                </div>
                <div className="sm:col-span-2">
                  <Button
                    type="submit"
                    disabled={busy}
                    data-testid="lead-submit"
                    className="w-full h-11 rounded-full text-white"
                    style={{ backgroundColor: "#010082" }}
                  >
                    {busy ? "Sending…" : "Request a call"}
                  </Button>
                  <p className="mt-3 text-xs text-stone-500 text-center">
                    We'll never share your info. Read our privacy note in the follow-up email.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200 py-8">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <img src={LOGO_URL} alt="" className="h-6 w-6 rounded-full" />
            <span>© {new Date().getFullYear()} Equitas Homes · 1% List Fee</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-stone-500">
            <Link to="/login" className="hover:text-stone-900">Agent portal</Link>
            <a href="#lead-form" className="hover:text-stone-900">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
